<template>
  <div>
    <div class="d-flex button-group"
         :class="{'justify-content-end': isNewSalesContract, 'justify-content-between': !isNewSalesContract}">
      <b-button v-if="!isNewSalesContract" @click="openHistoryModal">{{$t('buttons.common.history')}}</b-button>
      <history-modal-content></history-modal-content>
      <div class="button-group margin-0">
        <b-btn variant="outline-primary" size="sm"
               v-if="salesContractStatus === 'active' && currentRoutePermissions.change_salescontract"
               @click="changeSalesContractStatus('completed')">{{$t('buttons.common.to_complete')}}
        </b-btn>
        <b-btn variant="outline-primary" size="sm"
               v-if="(salesContractStatus === 'draft' || salesContractStatus === 'completed') && currentRoutePermissions.change_salescontract"
               @click="changeSalesContractStatus('active')">
          {{$t('buttons.common.to_active')}}
        </b-btn>
        <b-btn variant="outline-secondary" size="sm"
               v-if="salesContractStatus === 'active' && currentRoutePermissions.change_salescontract"
               @click="changeSalesContractStatus('draft')">{{$t('buttons.common.to_draft')}}
        </b-btn>
        <b-btn variant="outline-warning" size="sm" :class="{'hidden': isNewSalesContract}" id="saveAsNew">
          {{$t('buttons.common.save_as_new')}}
        </b-btn>
        <b-btn variant="outline-success" v-if="currentRoutePermissions.add_salescontract"
               @click="save" size="sm">{{ $t('buttons.common.save')}}
        </b-btn>
      </div>
      <b-popover target="saveAsNew" placement="bottom" ref="saveAsNewPopover" :show.sync="popoverSaveAsNewShow">
        <b-btn variant="warning" @click="saveAsNew('active')">Active</b-btn>
        <b-btn @click="saveAsNew('draft')">Draft</b-btn>
      </b-popover>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{salesContractLabel}}
      </h3>
      <div class="icons-wrapper">
        {{salesContractInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <sales-contract-position-modal :id="this.param">
    </sales-contract-position-modal>
    <div>
      <b-collapse id="collapse" v-model="showCollapse">
        <div class="filter-form">
          <b-row>
            <b-col>
              <label>{{$t('labels.sale_contract.contract_nr')}}</label>
              <input class="form-control" placeholder=""
                     v-model="salesContractData.contract_nr"
                     :class="{ required: !salesContractData.contract_nr && isLoadedData}"
                     :disabled="isCompletedContract">
            </b-col>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="salesContractData.buyer" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="buyers" :searchable="true"
                           :close-on-select="true"
                           :class="{ required: (!salesContractData.buyer && isLoadedData)}"
                           @close="selectBuyer"
                           :allowEmpty="false"
                           :disabled="isCompletedContract"
              ></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.agent')}}</label>
              <multiselect v-model="salesContractData.agent" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="agents" :searchable="true"
                           :close-on-select="true" :disabled="isCompletedContract"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.sale_contract.contract_duration')}}</label>
              <range-date-picker v-model="salesContractData.contract_duration" range lang="en"
                                 range-separator="/" :first-day-of-week="1"
                                 placeholder=""
                                 :disabled="isCompletedContract"></range-date-picker>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.delivery_addresses')}}</label>
              <multiselect v-model="salesContractData.delivery_addresses" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="delivery_addresses" :searchable="true"
                           :close-on-select="false" :multiple="true"
                           :disabled="!salesContractData.buyer || isCompletedContract"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.payment_terms')}}</label>
              <multiselect v-model="salesContractData.payment_term" :max-height="200"
                           placeholder="" label="short_name" track-by="short_name"
                           :options="payment_terms" :searchable="true"
                           :close-on-select="true" :disabled="isCompletedContract"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.seller')}}</label>
              <multiselect v-model="salesContractData.seller" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="sellers"
                           :close-on-select="true"
                           :class="{ required: (!salesContractData.seller && isLoadedData)}"
                           :allowEmpty="false" :disabled="isCompletedContract"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.delivery_method')}}</label>
              <multiselect v-model="salesContractData.delivery_method" :max-height="200"
                           placeholder="" label="name" track-by="name"
                           :options="delivery_methods" :searchable="true"
                           :close-on-select="true" :disabled="isCompletedContract"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.incoterms')}}</label>
              <multiselect v-model="salesContractData.incoterms" :max-height="200"
                           placeholder="" label="code" track-by="code"
                           :options="incoterms" :searchable="true"
                           :close-on-select="true" :disabled="isCompletedContract"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col offset="4">
              <label>{{$t('labels.common.currency')}}</label>
              <multiselect v-model="salesContractData.currency" :max-height="200" :searchable="true"
                           placeholder=""
                           label="code" track-by="code" :options="currencies"
                           :class="{ required: (!salesContractData.currency && isLoadedData)}"
                           :allowEmpty="false"
                           :disabled="isCompletedContract"
              ></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.notes')}}</label>
              <b-form-textarea v-model="salesContractData.notes" placeholder=""
                               :disabled="isCompletedContract">
              </b-form-textarea>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
    </div>
    <div v-if="!isNewSalesContract">
      <b-card no-body>
        <b-tabs card>
          <b-tab active>
            <template slot="title">
              <portal-target name="product_list_tab"></portal-target>
            </template>
            <portal to="product_list_tab">{{$t('tabs.sale_contact.product_list')}}</portal>
            <b-row>
              <b-col>
                <div class="d-flex justify-content-end button-group">
                  <b-btn variant="outline-success" v-if="currentRoutePermissions.change_salescontract"
                         size="sm" @click="openSalesContractPositionModal"
                         :disabled="isCompletedContract">
                    {{$t('buttons.sale_contract.add_position')}}
                  </b-btn>
                  <b-btn variant="outline-danger" v-if="currentRoutePermissions.change_salescontract"
                         size="sm" @click="deleteSalesContractItems"
                         :disabled="isCompletedContract">
                    {{$t('buttons.sale_contract.delete_position')}}
                  </b-btn>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <ag-grid-vue class="ag-theme-balham" :columnDefs="SalesContractItemsColumns"
                             :rowData="salesContractItems" :gridOptions="salesContractItemsGrid"
                             @rowDoubleClicked="loadSalesContractItemData" :style="gridHeight"
                             :postProcessPopup="postProcessPopup">
                </ag-grid-vue>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab @click="loadInvoiceListGroupingByOrders(true)">
            <template slot="title">
              <portal-target name="order_tab"></portal-target>
            </template>
            <portal to="order_tab">{{$t('tabs.sale_contact.orders')}}</portal>
            <ag-grid-vue class="ag-theme-balham" :columnDefs="invoiceListGroupingByOrdersColumns"
                         :gridOptions="invoiceListGroupingByOrdersGrid" :style="gridHeight"
                         :rowData="invoiceListGroupingByOrders"
                         :postProcessPopup="postProcessPopup">
            </ag-grid-vue>
          </b-tab>
          <b-tab @click="loadDocuments(true)">
            <template slot="title">
              <portal-target name="documents_tab"></portal-target>
            </template>
            <portal to="documents_tab">{{$t('tabs.common.documents')}}</portal>
            <app-documents :isCollapse="showCollapse" :documents="documents" :url="`${commonUrl}attachments/`"
                           @loadDocuments="loadDocuments"></app-documents>
          </b-tab>
          <b-tab @click="loadComments(true)">
            <template slot="title">
              <portal-target name="comments_tab"></portal-target>
            </template>
            <portal to="comments_tab">{{$t('tabs.common.comments')}}</portal>
            <app-comments :comments="comments" :url="`${commonUrl}comments/`"
                          @loadComments="loadComments"></app-comments>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapGetters, mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import RangeDatePicker from 'vue2-datepicker';
  import moment from 'moment';
  import {
    handlerResponse,
    setNotifyData,
    getArrayByProperty,
  } from '@/helpers/commonHelpers';
  import Comments from '@/components/common/Comments';
  import Documents from '@/components/common/Documents';
  import HistoryModalContent from '@/components/modals/HistoryModal';
  import SalesContractPositionModal from '@/components/modals/SalesContractPositionModal';
  import { gridMixins } from '@/mixins';
  import { DATE_FORMAT } from '@/constants';

  export default {
    mixins: [gridMixins],
    data() {
      return {
        isLoadedData: false,
        showCollapse: true,
        popoverSaveAsNewShow: false,
        salesContractData: {
          contract_nr: '',
          contract_duration: [],
          seller: null,
          currency: null,
          buyer: null,
          delivery_addresses: [],
          agent: null,
          delivery_method: null,
          payment_term: null,
          incoterms: null,
          notes: '',
          created_by: '',
          created: '',
          status: '',
        },
        SalesContractItemsColumns: [
          {width: 110, headerName: '', checkboxSelection: true},
          {
            headerName: 'Name',
            field: 'name'
          },
          {
            headerName: 'Article №',
            field: 'article_no'
          },
          {
            headerName: 'Product Name',
            field: 'product_name'
          },
          {
            headerName: 'Package size',
            field: 'pack_size',
            width: 70
          },
          {
            headerName: 'Package count',
            field: 'pack_count',
            width: 70
          },
          {
            headerName: 'Ordered',
            children: [
              {
                headerName: '',
                field: 'amount',
                width: 70
              },
              {
                headerName: '',
                field: 'measurement_value',
                width: 70
              }
            ]
          },
          {
            headerName: 'Price per',
            children: [
              {
                headerName: '',
                field: 'sale_price',
                width: 70
              },
              {
                headerName: '',
                field: 'measurement_for_price',
                width: 70
              }
            ]
          },
          {
            headerName: 'Price',
            field: 'full_sale_price',
            width: 70
          },
          {
            headerName: 'Package Type',
            field: 'package_type_name',
            width: 120
          },
          {
            headerName: 'Readiness',
            cellRenderer: 'ReadinessProgressBar',
            minWidth: 145
          },
          {
            headerName: 'Bundle size',
            field: 'bundle_size',
            width: 110
          }
        ],
        invoiceListGroupingByOrdersColumns: [
          {
            headerName: ' ',
            field: 'grouping',
            checkboxSelection: true,
            minWidth: 110,
            cellRenderer: 'agGroupCellRenderer'
          },
          {
            headerName: 'Order No.',
            field: 'descriptor',
            cellRenderer: 'OrderLink'
          },
          {
            headerName: 'Shop',
            field: 'partner_shipping_name',
            width: 110
          },
          {
            headerName: 'Invoice No.',
            field: 'invoice_number',
            cellRenderer: 'InvoiceLink',
            width: 110
          },
          {
            headerName: 'Invoice date',
            field: 'invoice_date',
            width: 110
          },
          {
            headerName: 'Shipped (m3)',
            field: 'shipped_m3',
            width: 120
          }
        ]
      }
    },
    components: {
      Multiselect,
      Datepicker,
      'ag-grid-vue': AgGridVue,
      'app-comments': Comments,
      'app-documents': Documents,
      HistoryModalContent,
      RangeDatePicker,
      SalesContractPositionModal
    },
    methods: {
      save() {
        const savedData = this.prepareSavedData();
        if (this.isNewSalesContract) {
          this.$http.post(`sales/sales_contracts/`, savedData)
            .then(handlerResponse).then((response) => {
            this.salesContractData = {...this.salesContractData, ...response};
            this.$notify(setNotifyData('Success', 'Sales Contract are saved', 'success'));
            this.$router.push({path: `/sales-contracts/${response.id}`});
            this.$store.dispatch('salesContract/loadSalesContractItems', {id: this.param});
          }).catch((error) => {
            for (let key in error.body) {
              this.$notify(setNotifyData(key, error.body[key][0], 'error'))
            }
          })
        } else {
          this.$http.put(`${this.commonUrl}`, savedData).then(handlerResponse).then(() => {
            this.$notify(setNotifyData('Success', 'Sales contract are saved!', 'success'));
          }).catch((error) => {
            for (let key in error.body) {
              this.$notify(setNotifyData(key, error.body[key][0], 'error'))
            }
          })
        }
      },
      getSalesContractInfo() {
        this.$http.get(`sales/sales_contracts/${this.param}/`).then(handlerResponse).then((response) => {
          this.salesContractData = {...this.salesContractData, ...response};
          this.$store.dispatch('location/setPathName', response.contract_nr);
          this.loadContactsForBuyer();
          this.isLoadedData = true;
        });
      },
      loadInvoiceListGroupingByOrders(cacheFlag) {
        if (cacheFlag) {
          this.$store.cache.dispatch('salesContract/loadInvoiceListGroupingByOrders', {id: this.param});
        } else {
          this.$store.dispatch('salesContract/loadInvoiceListGroupingByOrders', {id: this.param});
        }
      },
      loadComments(cacheFlag) {
        if (cacheFlag) {
          this.$store.cache.dispatch('salesContract/loadCommentsList', {id: this.param});
        } else {
          this.$store.dispatch('salesContract/loadCommentsList', {id: this.param});
        }
      },
      loadDocuments(cacheFlag) {
        if (cacheFlag) {
          this.$store.cache.dispatch('salesContract/loadDocumentsList', {id: this.param});
        } else {
          this.$store.dispatch('salesContract/loadDocumentsList', {id: this.param});
        }
      },
      saveAsNew(status) {
        this.popoverSaveAsNewShow = false;
        const savedData = this.prepareSavedData();
        this.$http.post(`${this.commonUrl}save_as_new/`, {
          ...savedData,
          status
        }).then(handlerResponse).then((response) => {
          this.$router.push({path: `/sales-contracts/${response.id}`});
          this.salesContractData = {...this.salesContractData, ...response};
          this.$store.dispatch('salesContract/loadSalesContractItems', {id: this.param});
        });
      },
      prepareSavedData() {
        return {
          buyer: this.salesContractData.buyer && this.salesContractData.buyer.id,
          seller: this.salesContractData.seller && this.salesContractData.seller.id,
          contract_nr: this.salesContractData.contract_nr,
          currency: this.salesContractData.currency && this.salesContractData.currency.id,
          date_start: this.salesContractData.contract_duration && this.salesContractData.contract_duration[0] ? moment(this.salesContractData.contract_duration[0]).format(DATE_FORMAT) : null,
          date_end: this.salesContractData.contract_duration && this.salesContractData.contract_duration[1] ? moment(this.salesContractData.contract_duration[1]).format(DATE_FORMAT) : null,
          agent: this.salesContractData.agent && this.salesContractData.agent.id,
          delivery_addresses: getArrayByProperty(this.salesContractData.delivery_addresses, 'id'),
          delivery_method: this.salesContractData.delivery_method && this.salesContractData.delivery_method.id,
          payment_term: this.salesContractData.payment_term && this.salesContractData.payment_term.id,
          incoterms: this.salesContractData.incoterms && this.salesContractData.incoterms.id,
          notes: this.salesContractData.notes
        }
      },
      changeSalesContractStatus(status) {
        this.$http.put(`${this.commonUrl}`, {status}).then(handlerResponse).then((response) => {
          this.salesContractData = {...this.salesContractData, ...response};
          this.$notify(setNotifyData('Success', 'Sales contract status has been successfully changed!', 'success'));
        }).catch((error) => {
          console.log(error);
        })
      },
      openSalesContractPositionModal(salesContractItemData) {
        this.$root.$emit('openSalesContractPositionModal', salesContractItemData);
      },
      loadSalesContractItemData(params) {
        if (this.currentRoutePermissions.change_salescontract) {
          if (!this.isCompletedContract) {
            const salesContractItemId = params.data.id;
            this.$http.get(`${this.commonUrl}sales_contract_items/${salesContractItemId}/`).then(handlerResponse).then((response) => {
              this.openSalesContractPositionModal(response);
            });
          }
        }
      },
      deleteSalesContractItems() {
        const ids = getArrayByProperty(this.salesContractItemsGrid.api.getSelectedRows(), 'id');
        if (ids.length) {
          this.$http.delete(`${this.commonUrl}sales_contract_items/`, {body: {ids}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
          }).then(() => {
            this.$store.dispatch('salesContract/loadSalesContractItems', {id: this.param});
          }).catch((response) => {
            this.$notify(setNotifyData('', response.body, 'error'));
          });
        } else {
          this.$notify(setNotifyData('Select sales contracts for removing', '', 'warn'));
        }
      },
      loadContactsForBuyer() {
        if (this.salesContractData.buyer && this.salesContractData.buyer.id) {
          const partner_id = this.salesContractData.buyer.id
          this.$store.dispatch('woodware/getDeliveryAddresses', {partner_id});
        }
      },
      clearContactFields() {
        this.salesContractData.delivery_addresses = [];
      },
      selectBuyer() {
        this.clearContactFields();
        this.loadContactsForBuyer();
      },
      async fetchInitializeData() {
        this.$store.cache.dispatch('woodware/getDeliveryMethods');
        this.$store.dispatch('woodware/getPaymentTerms');
        this.$store.dispatch('woodware/getPartners', {is_active: true});
        this.$store.dispatch('generic/loadIncoterms');
        this.$store.cache.dispatch('generic/getCurrencies');
        if (!this.isNewSalesContract) {
          this.getSalesContractInfo();
          this.$store.dispatch('salesContract/loadSalesContractItems', {id: this.param});
        } else {
          this.isLoadedData = true;
        }
      },
      clearData() {
        this.$store.cache.delete('salesContract/loadDocumentsList', {id: this.param});
        this.$store.cache.delete('salesContract/loadCommentsList', {id: this.param});
        this.$store.cache.delete('salesContract/loadInvoiceListGroupingByOrders', {id: this.param});
        if (this.comments.length) {
          this.$store.commit('salesContract/setCommentsList', []);
        }
        if (this.documents.length) {
          this.$store.commit('salesContract/setDocumentsList', []);
        }
        this.$store.commit('salesContract/setSalesContractItems', []);
      },
      openHistoryModal() {
        this.$root.$emit('openHistoryModal', this.historySettings);
      }
    },
    computed: {
      ...mapGetters('woodware', {
        delivery_methods: 'delivery_methods',
        delivery_addresses: 'delivery_addresses',
        payment_terms: 'payment_terms',
        partners: 'partners',
        buyers: 'buyers',
        sellers: 'sellers',
        agents: 'agents'
      }),
      ...mapState('generic', ['currencies', 'incoterms', 'currentRoutePermissions']),
      ...mapGetters('salesContract', {
        comments: 'comments',
        documents: 'documents',
        salesContractItems: 'salesContractItems',
        salesContractItemsGrid: 'salesContractItemsGrid',
        invoiceListGroupingByOrders: 'invoiceListGroupingByOrders',
        invoiceListGroupingByOrdersGrid: 'invoiceListGroupingByOrdersGrid'
      }),
      param() {
        return this.$route.params.id;
      },
      isNewSalesContract() {
        return this.param === 'new';
      },
      commonUrl() {
        return `sales/sales_contracts/${this.param}/`;
      },
      salesContractLabel() {
        return this.isNewSalesContract ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.contract')}` :
          `${this.$i18n.t('common_content.contract')} / ${this.salesContractData.contract_nr} - ${this.salesContractStatus}`;
      },
      salesContractInfo() {
        return this.isNewSalesContract ? '' : `${this.salesContractData.created} / ${this.$i18n.t('common_content.creator')}
        : ${this.salesContractData.created_by}`
      },
      salesContractStatus() {
        return !this.isNewSalesContract ? this.salesContractData.status : '';
      },
      isCompletedContract() {
        return this.salesContractStatus === 'completed';
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 565px)'} : {height: 'calc(100vh - 300px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 300px)'};
        }
      },
      historySettings() {
        return {url: `sales_contract/?id=${this.param}`, title: this.salesContractLabel.split('/')[1]}
      }
    },
    created() {
      this.fetchInitializeData();
    },
    beforeRouteUpdate(to, from, next) {
      next();
      this.fetchInitializeData();
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    }
  }
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/sales/contracts/SalesContract.vue